// src/pages/search.tsx
import { useState, useEffect, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useAuth } from '../lib/auth'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { DatePicker } from '@/components/ui/date-picker'
import { parseNaturalQuery } from '@/lib/nlp-processor'
import { DuffelClient } from '@/lib/duffel-client'
import { Combobox } from '@/components/ui/combobox'
import { searchAirportCodes } from '@/lib/airport-codes'
import { searchPlaces } from '@/lib/duffel-places'
import { Card } from '@/components/ui/card'
import { saveSearch, normalizeCityName } from '@/lib/saved-searches'
import { ExpandableFlightCard } from '@/components/travel/expandable-flight-card'
import { CollapsibleItineraryPreview } from '@/components/travel/CollapsibleItineraryPreview'
import { RecentSearches } from '@/components/travel/RecentSearches'
import type { Flight, ItineraryResponse } from '@/types/travel'
import type { SavedSearch } from '@/types/profile'

export default function SearchPage() {
  // State declarations
  const [departureDate, setDepartureDate] = useState<Date>()
  const [returnDate, setReturnDate] = useState<Date>()
  const [adults, setAdults] = useState(1)
  const [kids, setKids] = useState(0)
  const [destination, setDestination] = useState("")
  const [origin, setOrigin] = useState("")
  const [naturalQuery, setNaturalQuery] = useState("")
  const [originOptions, setOriginOptions] = useState<{ value: string; label: string }[]>([])
  const [destinationOptions, setDestinationOptions] = useState<{ value: string; label: string }[]>([])
  const [recommendedFlights, setRecommendedFlights] = useState<Flight[]>([])
  const [isProcessing, setIsProcessing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [itinerary, setItinerary] = useState<ItineraryResponse | null>(null)
  const [isGeneratingItinerary, setIsGeneratingItinerary] = useState(false)
  const [expandedFlightId, setExpandedFlightId] = useState<string | null>(null)
  const API_URL = import.meta.env.DEV ? 'http://localhost:3000' : 'https://backend-test-ldkc.onrender.com';
  const BEARER_API_KEY = import.meta.env.VITE_API_TOKEN

  const { user } = useAuth()
  const [searchParams] = useSearchParams()
  const pendingAutoSearch = useRef(false)

  /** Parse a date-like string; return undefined if invalid. */
  const parseDateSafe = (raw: string): Date | undefined => {
    const d = new Date(raw)
    return Number.isNaN(d.getTime()) ? undefined : d
  }

  /** Parse an int; return undefined if NaN. */
  const parseIntSafe = (raw: string): number | undefined => {
    const n = parseInt(raw, 10)
    return Number.isNaN(n) ? undefined : n
  }

  // VOY-412: Pre-populate form from URL params (e.g. from profile saved search click).
  // State setters are async, so we set a pendingAutoSearch flag and fire the search
  // from a second effect once state has actually applied — avoids the handleSearch
  // guard clause firing on stale state.
  //
  // originLabel/destinationLabel are pre-fetched by RecentSearchesRow so the
  // Combobox shows "Buenos Aires, Argentina (EZE)" rather than a bare code.
  useEffect(() => {
    const originParam = searchParams.get('origin')
    const destinationParam = searchParams.get('destination')
    const originLabelParam = searchParams.get('originLabel')
    const destinationLabelParam = searchParams.get('destinationLabel')
    const departureDateParam = searchParams.get('departureDate')
    const returnDateParam = searchParams.get('returnDate')
    const adultsParam = searchParams.get('adults')
    const childrenParam = searchParams.get('children')
    const autoSearchParam = searchParams.get('autoSearch')

    if (originParam) {
      setOrigin(originParam)
      // Hydrate Combobox options so the display label shows instead of raw code.
      if (originLabelParam) {
        setOriginOptions([{ value: originParam, label: originLabelParam }])
      } else {
        // Fallback: resolve the label from the airport service.
        searchAirportCodes(originParam)
          .then(results => {
            const match = results.find(a => a.code.toUpperCase() === originParam.toUpperCase()) ?? results[0]
            if (match) {
              setOriginOptions([{ value: match.code, label: `${match.city}, ${match.country} (${match.code})` }])
            }
          })
          .catch(() => { /* non-fatal */ })
      }
    }
    if (destinationParam) {
      setDestination(destinationParam)
      if (destinationLabelParam) {
        setDestinationOptions([{ value: destinationParam, label: destinationLabelParam }])
      } else {
        searchAirportCodes(destinationParam)
          .then(results => {
            const match = results.find(a => a.code.toUpperCase() === destinationParam.toUpperCase()) ?? results[0]
            if (match) {
              setDestinationOptions([{ value: match.code, label: `${match.city}, ${match.country} (${match.code})` }])
            }
          })
          .catch(() => { /* non-fatal */ })
      }
    }
    if (departureDateParam) {
      const d = parseDateSafe(departureDateParam)
      if (d) setDepartureDate(d)
    }
    if (returnDateParam) {
      const d = parseDateSafe(returnDateParam)
      if (d) setReturnDate(d)
    }
    if (adultsParam) {
      const n = parseIntSafe(adultsParam)
      if (n !== undefined) setAdults(n)
    }
    if (childrenParam) {
      const n = parseIntSafe(childrenParam)
      if (n !== undefined) setKids(n)
    }

    if (autoSearchParam === 'true' && originParam && destinationParam && departureDateParam) {
      pendingAutoSearch.current = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Fires the auto-search only after state from the URL-params effect has settled.
  useEffect(() => {
    if (pendingAutoSearch.current && origin && destination && departureDate) {
      pendingAutoSearch.current = false
      handleSearch()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [origin, destination, departureDate])

  // Airport search handlers
  const handleOriginSearch = async (query: string) => {
    try {
      // VOY-401: Duffel Places Suggestions with Supabase fallback
      const results = await searchPlaces(query)
      setOriginOptions(
        results.map(airport => ({
          value: airport.code,
          label: `${airport.city}, ${airport.country} (${airport.code})`
        }))
      )
    } catch (error) {
      console.error('Error searching origin airports:', error)
    }
  }

  const handleDestinationSearch = async (query: string) => {
    try {
      // VOY-401: Duffel Places Suggestions with Supabase fallback
      const results = await searchPlaces(query)
      setDestinationOptions(
        results.map(airport => ({
          value: airport.code,
          label: `${airport.city}, ${airport.country} (${airport.code})`
        }))
      )
    } catch (error) {
      console.error('Error searching destination airports:', error)
    }
  }

  const handleNaturalQueryChange = async (query: string) => {
    setNaturalQuery(query)
   //
    if (query.length < 25) return
    
    const parsed = parseNaturalQuery(query)
    
    if (parsed.destination) {
      try {
        const results = await searchAirportCodes(parsed.destination)
        if (results && results.length > 0) {
          const validResults = results.filter(airport => 
            airport.city.toLowerCase().includes(parsed.destination!.toLowerCase()) ||
            airport.country.toLowerCase().includes(parsed.destination!.toLowerCase())
          )

          if (validResults.length > 0) {
            const bestMatch = validResults[0]
            setDestination(bestMatch.code)
            setDestinationOptions(validResults.map(airport => ({
              value: airport.code,
              label: `${airport.city}, ${airport.country} (${airport.code})`
            })))
          }
        }
      } catch (error) {
        console.error('Error searching destination:', error)
      }
    }

    if (parsed.origin) {
      try {
        const results = await searchAirportCodes(parsed.origin)
        if (results && results.length > 0) {
          const validResults = results.filter(airport => 
            airport.city.toLowerCase().includes(parsed.origin!.toLowerCase()) ||
            airport.country.toLowerCase().includes(parsed.origin!.toLowerCase())
          )

          if (validResults.length > 0) {
            const bestMatch = validResults[0]
            setOrigin(bestMatch.code)
            setOriginOptions(validResults.map(airport => ({
              value: airport.code,
              label: `${airport.city}, ${airport.country} (${airport.code})`
            })))
          }
        }
      } catch (error) {
        console.error('Error searching origin:', error)
      }
    }

    if (parsed.departureDate) {
      setDepartureDate(parsed.departureDate)
    }
    
    if (parsed.returnDate) {
      setReturnDate(parsed.returnDate)
    }

    if (parsed.adults && !isNaN(parsed.adults)) {
      setAdults(parsed.adults)
    }
    
    if (parsed.children && !isNaN(parsed.children)) {
      setKids(parsed.children)
    }
  }
  const handleSearch = async () => {
    setError(null)
    setItinerary(null)
    if (!departureDate || !origin || !destination) {
      setError('Please enter origin, destination and departure date')
      return
    }

    setIsProcessing(true)
    setIsGeneratingItinerary(true)
    
    try {
      const searchParams = {
        origin,
        destination,
        departureDate,
        returnDate,
        passengers: {
          adults: Math.max(1, adults),
          children: Math.max(0, kids)
        }
      }

      const flights = await DuffelClient.searchFlights(searchParams)
      setRecommendedFlights(flights)

      // Generate itinerary using OpenAI
      try {
        console.log('Sending itinerary request with:', {
          destination,
          departureDate,
          returnDate,
          passengers: {
            adults: Math.max(1, adults),
            children: Math.max(0, kids)
          }
        });

        const response = await fetch(`${API_URL}/api/itinerary/generate`, {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${BEARER_API_KEY}`
          },
          body: JSON.stringify({
            destination,
            departureDate: departureDate.toISOString(),
            returnDate: returnDate?.toISOString(),
            passengers: {
              adults: Math.max(1, adults),
              children: Math.max(0, kids)
            }
          })
        });

        if (!response.ok) {
          throw new Error('Failed to generate itinerary')
        }
        
        const data = await response.json()
        console.log('Received itinerary:', data);
        
        // Parse the response to handle new format
        if (data && typeof data === 'object' && 'type' in data && 'data' in data) {
          setItinerary(data as ItineraryResponse)
        } else if (data.itinerary) {
          // Legacy format - wrap in new format
          setItinerary({
            type: 'html',
            data: data.itinerary
          })
        } else {
          setItinerary(null)
        }
      } catch (error) {
        console.error('Failed to generate itinerary:', error)
        setItinerary(null)
      } finally {
        setIsGeneratingItinerary(false)
      }

      if (user) {
        // VOY-411: pass enrichment so downstream AI agents get structured context.
        // City labels come from the typeahead options already loaded into state.
        const prices = flights
          .map(f => Number(f.price))
          .filter(n => Number.isFinite(n) && n > 0)
        const lowestPrice = prices.length > 0 ? Math.min(...prices) : undefined
        const originLabel = originOptions.find(o => o.value === origin)?.label
        const destinationLabel = destinationOptions.find(o => o.value === destination)?.label
        await saveSearch(user.id, 'flight', searchParams, {
          originCityName: normalizeCityName(originLabel),
          destinationCityName: normalizeCityName(destinationLabel),
          resultsCount: flights.length,
          lowestPriceSeen: lowestPrice,
          searchSource: 'search',
        })
      }
    } catch (error) {
      console.error('Search failed:', error)
      setError(error instanceof Error ? error.message : 'Failed to search flights')
    } finally {
      setIsProcessing(false)
    }
  }

  const handleRecentSearchSelect = (search: SavedSearch) => {
    if (search.search_type === 'flight') {
      const { origin: searchOrigin, destination: searchDestination, departureDate: depDate, returnDate: retDate, passengers } = search.query
      setOrigin(searchOrigin)
      setDestination(searchDestination)
      if (depDate) {
        const d = parseDateSafe(depDate)
        if (d) setDepartureDate(d)
      }
      if (retDate) {
        const d = parseDateSafe(retDate)
        if (d) setReturnDate(d)
      }
      if (passengers?.adults !== undefined) setAdults(passengers.adults)
      setKids(passengers?.children || 0)
    }
  }

  return (
    <div className="container mx-auto py-6">
      <div className="flex flex-col gap-6">
        {/* Search Form Section */}
        <div className="w-full">
          <Card className="p-6 shadow-md">
                <div className="space-y-8">
                  {/* Step 1: Natural Language Input */}
                  <div className="relative">
                    <div className="absolute -left-4 top-0 flex h-8 w-8 items-center justify-center rounded-full bg-primary text-primary-foreground">
                      1
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium pl-6">
                        Describe your trip
                      </label>
                      <Textarea
                        placeholder="Example: I want to fly from Paris to Tokyo from December 20 to December 30 with 2 adults and 1 kid"
                        value={naturalQuery}
                        onChange={(e) => handleNaturalQueryChange(e.target.value)}
                        className="min-h-[100px] rounded-lg border-input focus-visible:ring-1 focus-visible:ring-ring resize-none"
                      />
                    </div>
                  </div>

                  {/* Step 2: Route Selection */}
                  <div className="relative">
                    <div className="absolute -left-4 top-0 flex h-8 w-8 items-center justify-center rounded-full bg-primary text-primary-foreground">
                      2
                    </div>
                    <div className="space-y-4">
                      <label className="text-sm font-medium pl-6">
                        Select your route
                      </label>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <Combobox
                          placeholder="From (e.g. London, Paris)"
                          searchPlaceholder="Search cities or airports..."
                          value={origin}
                          onValueChange={setOrigin}
                          onSearch={handleOriginSearch}
                          options={originOptions}
                          className="w-full"
                        />
                        <Combobox
                          placeholder="To (e.g. Tokyo, New York)"
                          searchPlaceholder="Search cities or airports..."
                          value={destination}
                          onValueChange={setDestination}
                          onSearch={handleDestinationSearch}
                          options={destinationOptions}
                          className="w-full"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Step 3: Dates and Passengers */}
                  <div className="relative">
                    <div className="absolute -left-4 top-0 flex h-8 w-8 items-center justify-center rounded-full bg-primary text-primary-foreground">
                      3
                    </div>
                    <div className="space-y-4">
                      <label className="text-sm font-medium pl-6">
                        Choose dates and passengers
                      </label>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <DatePicker 
                          selected={departureDate}
                          onChange={setDepartureDate}
                          placeholder="Select departure date"
                          className="w-full"
                          minDate={new Date()}
                        />
                        <DatePicker 
                          selected={returnDate}
                          onChange={setReturnDate}
                          placeholder="Select return date (optional)"
                          className="w-full"
                          minDate={departureDate}
                        />
                      </div>
                      
                      <div className="flex gap-2">
                        <Input
                          type="number" 
                          value={adults || ''}
                          onChange={(e) => setAdults(Math.max(1, parseInt(e.target.value) || 1))}
                          placeholder="Adults"
                          min={1}
                          className="rounded-lg"
                        />
                        <Input
                          type="number"
                          value={kids || ''}
                          onChange={(e) => setKids(Math.max(0, parseInt(e.target.value) || 0))}
                          placeholder="Kids"
                          min={0}
                          className="rounded-lg"
                        />
                      </div>
                    </div>
                  </div>
                  
                  {error && (
                    <p className="text-sm text-destructive mt-4">
                      {error}
                    </p>
                  )}
                  
                  <Button 
                    onClick={handleSearch}
                    disabled={isProcessing}
                    className="w-full bg-gradient-to-r from-primary to-primary/90 hover:from-primary/90 hover:to-primary/80 transition-all"
                    size="lg"
                  >
                    {isProcessing ? (
                      <div className="flex items-center gap-2">
                        <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                        <span>Processing...</span>
                      </div>
                    ) : (
                      'Search Flights'
                    )}
                  </Button>
                </div>
              </Card>
        </div>

        {/* Collapsible Itinerary Section - Above Results */}
        {(itinerary || isGeneratingItinerary) && (
          <div className="w-full">
            <CollapsibleItineraryPreview 
              isLoading={isGeneratingItinerary}
              itinerary={itinerary}
            />
          </div>
        )}

        {/* Flight Results Section */}
        <div className="flex flex-col lg:flex-row gap-6">
          <div className="flex-1 space-y-4">
            {isProcessing && (
              <div className="flex items-center justify-center py-12">
                <div className="flex flex-col items-center gap-4">
                  <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
                  <p className="text-sm text-muted-foreground">Searching for the best flights...</p>
                </div>
              </div>
            )}
            {recommendedFlights.map((flight) => (
              <ExpandableFlightCard
                key={flight.id}
                flight={flight}
                isExpanded={expandedFlightId === flight.id}
                onExpandChange={(expanded) => {
                  setExpandedFlightId(expanded ? flight.id : null);
                }}
                itinerary={itinerary || undefined}
                onSelect={(selectedFlight) => {
                  console.log('Selected flight for booking:', selectedFlight)
                }}
              />
            ))}
            
            {recommendedFlights.length === 0 && !isProcessing && (
              <p className="text-center text-muted-foreground">
                No flights found. Try adjusting your search criteria.
              </p>
            )}
          </div>
          
          {/* Recent Searches Sidebar */}
          <div className="lg:w-80">
            <RecentSearches onSearchSelect={handleRecentSearchSelect} />
          </div>
        </div>
      </div>
    </div>
  )
}